import type { ExportDressingOptions } from '@mss_creator/shared';
import { TextField } from '@pms-digital/design-system';
import { isControlVisible } from './export-format-controls';
import type { SegmentOption } from './export-segmented-control';
import { ExportSelectField } from './export-select-field';
import type { ExportFormat } from './export-selection';
import { ExportToggleField } from './export-toggle-field';

interface ExportDressingControlsProps {
  /**
   * Picks the controls shown, through the per-format visibility table — page
   * numbers and assembly marks only mean something on paginated output.
   */
  format: ExportFormat;
  dressing: ExportDressingOptions;
  onChange: (dressing: ExportDressingOptions) => void;
}

/** Matches the wire's `footerText` cap. */
const FOOTER_TEXT_MAX_LENGTH = 200;

const SIGNATURE_OPTIONS: readonly SegmentOption<ExportDressingOptions['pmsSignature']>[] = [
  { value: 'genere-avec', label: '« Généré avec » + logo' },
  { value: 'wordmark', label: 'Logo seul' },
  { value: 'anneau', label: 'Anneau seul' },
  { value: 'aucune', label: 'Aucune' },
];

/** The « Habillage » category: what frames the table on the page. */
export function ExportDressingControls({
  format,
  dressing,
  onChange,
}: ExportDressingControlsProps) {
  return (
    <>
      <ExportToggleField
        label="Logo de l'entreprise"
        checked={dressing.companyLogo}
        onChange={(companyLogo) => onChange({ ...dressing, companyLogo })}
      />
      <ExportToggleField
        label="Bloc titre"
        checked={dressing.titleBlock}
        onChange={(titleBlock) => onChange({ ...dressing, titleBlock })}
      />
      <ExportToggleField
        label="Dates"
        checked={dressing.dates}
        onChange={(dates) => onChange({ ...dressing, dates })}
      />
      {isControlVisible(format, 'pageNumbers') && (
        <ExportToggleField
          label="Numérotation des pages"
          checked={dressing.pageNumbers}
          onChange={(pageNumbers) => onChange({ ...dressing, pageNumbers })}
        />
      )}
      {/* An emptied field goes back to null — the wire's "no mention" —
          rather than an empty string the renderer would have to special-case. */}
      <TextField
        label="Mention libre"
        value={dressing.footerText ?? ''}
        maxLength={FOOTER_TEXT_MAX_LENGTH}
        onChange={(event) =>
          onChange({
            ...dressing,
            footerText: event.target.value === '' ? null : event.target.value,
          })
        }
      />
      <ExportSelectField
        label="Signature PMS"
        value={dressing.pmsSignature}
        options={SIGNATURE_OPTIONS}
        onChange={(pmsSignature) => onChange({ ...dressing, pmsSignature })}
      />
      {isControlVisible(format, 'assemblyMarks') && (
        <ExportToggleField
          label="Repères d'assemblage"
          checked={dressing.assemblyMarks}
          onChange={(assemblyMarks) => onChange({ ...dressing, assemblyMarks })}
        />
      )}
    </>
  );
}
